// Icon design tokens — semantic registry mapping a functional name to a canonical
// FontAwesome 6 class string. This is the single source of truth for iconography:
// one concept = one glyph, always with an FA6 style prefix (`fa-solid`/`fa-regular`),
// never the legacy `fa fa-*` alias or the FA4 `fa-*-o` variants.
//
// Usage:
//   import { Icon } from '@ui';           <Icon name="edit" />
//   import { ICONS } from '@/constants/icons';   <AppButton icon={ICONS.add} />
//
// The value is a full class string so it can be consumed both by <Icon> and by the
// components that accept a raw FA class prop (AppButton, IconBadge, Tab).
export const ICONS = {
  // ===== Actions: CRUD & form =====
  edit: 'fa-solid fa-pen-to-square', // was: pencil / edit / pen / pencil-square-o
  editUser: 'fa-solid fa-user-pen',
  delete: 'fa-solid fa-trash',
  add: 'fa-solid fa-plus',
  addCircle: 'fa-solid fa-circle-plus', // was: plus-circle
  addUser: 'fa-solid fa-user-plus',
  addDate: 'fa-solid fa-calendar-plus',
  addFolder: 'fa-solid fa-folder-plus',
  remove: 'fa-solid fa-minus',
  removeUser: 'fa-solid fa-user-minus',
  save: 'fa-solid fa-floppy-disk', // was: fa-save (alias)
  copy: 'fa-solid fa-copy',
  close: 'fa-solid fa-xmark', // was: fa-times
  closeCircle: 'fa-solid fa-circle-xmark',
  cancel: 'fa-solid fa-xmark',
  ban: 'fa-solid fa-ban',
  confirm: 'fa-solid fa-check',
  check: 'fa-solid fa-check',
  checkCircle: 'fa-solid fa-circle-check',
  checkSquare: 'fa-solid fa-square-check',
  clipboardCheck: 'fa-solid fa-clipboard-check',
  // Censimento mezzo: la scheda è un clipboard, il punto zero una bandierina.
  inspection: 'fa-solid fa-clipboard-check',
  zeroPoint: 'fa-solid fa-flag-checkered',
  tyre: 'fa-solid fa-circle-dot',
  deadlines: 'fa-solid fa-calendar-xmark',
  search: 'fa-solid fa-magnifying-glass', // was: fa-search
  filters: 'fa-solid fa-sliders',
  checklist: 'fa-solid fa-list-check',
  refresh: 'fa-solid fa-rotate-right', // was: refresh / sync / rotate / rotate-right
  undo: 'fa-solid fa-rotate-left', // was: rotate-left / undo
  rotate: 'fa-solid fa-rotate',
  settings: 'fa-solid fa-gear', // was: cog / cogs
  userSettings: 'fa-solid fa-user-gear',
  link: 'fa-solid fa-link',
  unlink: 'fa-solid fa-link-slash', // was: fa-unlink
  download: 'fa-solid fa-download',
  upload: 'fa-solid fa-upload',
  cloudUpload: 'fa-solid fa-cloud-arrow-up',
  import: 'fa-solid fa-file-import',
  export: 'fa-solid fa-file-export',
  logout: 'fa-solid fa-right-from-bracket', // was: sign-out-alt
  view: 'fa-solid fa-eye',
  send: 'fa-solid fa-paper-plane',
  play: 'fa-solid fa-play',
  playCircle: 'fa-solid fa-circle-play', // was: play-circle
  pause: 'fa-solid fa-pause',
  externalLink: 'fa-solid fa-arrow-up-right-from-square',

  // ===== Status & feedback =====
  warning: 'fa-solid fa-triangle-exclamation', // was: exclamation-triangle
  info: 'fa-solid fa-circle-info', // was: info-circle
  error: 'fa-solid fa-circle-exclamation',
  success: 'fa-solid fa-circle-check',
  loading: 'fa-solid fa-spinner fa-spin',
  empty: 'fa-solid fa-inbox',
  notification: 'fa-solid fa-bell',
  notificationOff: 'fa-solid fa-bell-slash',
  question: 'fa-solid fa-question',
  target: 'fa-solid fa-bullseye',
  bolt: 'fa-solid fa-bolt',
  star: 'fa-solid fa-star',

  // ===== People & identity =====
  user: 'fa-solid fa-user',
  userCircle: 'fa-solid fa-circle-user', // was: user-circle
  users: 'fa-solid fa-users',
  userGroup: 'fa-solid fa-user-group', // was: user-friends
  peopleGroup: 'fa-solid fa-people-group',
  manager: 'fa-solid fa-user-tie',
  userDisabled: 'fa-solid fa-user-slash', // was: user-times
  // Passenger absent (car pooling absences); `presence` takes the absence away.
  absence: 'fa-solid fa-user-slash',
  presence: 'fa-solid fa-user-check',
  userShield: 'fa-solid fa-user-shield',
  userTag: 'fa-solid fa-user-tag',
  driver: 'fa-solid fa-id-card-clip', // free-set: fa-steering-wheel is FA Pro-only (renders empty)
  nurse: 'fa-solid fa-user-nurse',
  idCard: 'fa-solid fa-id-card',
  idBadge: 'fa-solid fa-id-badge',
  addressCard: 'fa-solid fa-address-card',
  addressBook: 'fa-solid fa-address-book',

  // ===== Organizations & places =====
  company: 'fa-solid fa-building',
  hospital: 'fa-solid fa-hospital',
  warehouse: 'fa-solid fa-warehouse',
  briefcase: 'fa-solid fa-briefcase',
  parking: 'fa-solid fa-square-parking',
  chargingStation: 'fa-solid fa-charging-station',

  // ===== Vehicles & mobility =====
  vehicle: 'fa-solid fa-van-shuttle', // was: shuttle-van
  bus: 'fa-solid fa-bus',
  car: 'fa-solid fa-car',
  ambulance: 'fa-solid fa-truck-medical', // was: fa-ambulance (alias)
  train: 'fa-solid fa-train',
  plane: 'fa-solid fa-plane',
  truckFast: 'fa-solid fa-truck-fast',
  wheelchair: 'fa-solid fa-wheelchair',
  wheelchairMove: 'fa-solid fa-wheelchair-move',
  walking: 'fa-solid fa-person-walking',
  walkingArrow: 'fa-solid fa-person-walking-arrow-right',
  handsHoldingCircle: 'fa-solid fa-hands-holding-circle',

  // ===== Trip / route / map =====
  trip: 'fa-solid fa-route',
  road: 'fa-solid fa-road',
  roadBarrier: 'fa-solid fa-road-barrier',
  destination: 'fa-solid fa-flag-checkered',
  location: 'fa-solid fa-location-dot', // was: map-marker-alt / map-marker / map-pin
  locationArrow: 'fa-solid fa-location-arrow',
  route: 'fa-solid fa-route',
  expand: 'fa-solid fa-expand',
  map: 'fa-solid fa-map',
  mapLocation: 'fa-solid fa-map-location-dot',
  polygon: 'fa-solid fa-draw-polygon',
  swap: 'fa-solid fa-right-left', // was: exchange-alt / exchange
  seat: 'fa-solid fa-chair',
  bed: 'fa-solid fa-bed',
  // Barriere architettoniche del punto di presa/consegna: le scale, non la
  // transenna stradale (`roadBarrier`, che è un'altra cosa).
  stairs: 'fa-solid fa-stairs',
  // Requisiti operativi di un servizio (vedi `modules/logic/driverCapabilities`).
  stretcher: 'fa-solid fa-bed-pulse',
  oxygen: 'fa-solid fa-lungs',

  // ===== Time =====
  calendar: 'fa-solid fa-calendar',
  calendarCheck: 'fa-solid fa-calendar-check',
  // Momento in cui un record è ENTRATO (data inserimento), distinto da
  // `calendar` = quando il servizio si svolge (data esecuzione).
  calendarPlus: 'fa-solid fa-calendar-plus',
  calendarTimes: 'fa-solid fa-calendar-xmark', // was: calendar-times
  calendarDay: 'fa-solid fa-calendar-day',
  calendarDays: 'fa-solid fa-calendar-days',
  calendarWeek: 'fa-solid fa-calendar-week',
  // Per-date exception of a daily trip ("eccezione"): a branch off the
  // recurring series. Distinct from `calendarTimes` (absence) and `layerGroup`
  // (pooling group).
  exception: 'fa-solid fa-code-branch',
  clock: 'fa-solid fa-clock', // was: clock-o
  history: 'fa-solid fa-clock-rotate-left', // was: fa-history
  hourglass: 'fa-solid fa-hourglass-half',
  stopwatch: 'fa-solid fa-stopwatch',
  plannedTime: 'fa-solid fa-business-time',

  // ===== Documents & files =====
  invoice: 'fa-solid fa-file-invoice',
  invoiceDollar: 'fa-solid fa-file-invoice-dollar',
  receipt: 'fa-solid fa-receipt',
  signature: 'fa-solid fa-file-signature',
  filePdf: 'fa-solid fa-file-pdf', // was: file-pdf-o
  fileExcel: 'fa-solid fa-file-excel', // was: file-excel-o
  fileWord: 'fa-solid fa-file-word',
  fileCsv: 'fa-solid fa-file-csv',
  fileImage: 'fa-solid fa-file-image',
  fileZip: 'fa-solid fa-file-zipper',
  fileText: 'fa-solid fa-file-lines', // was: file-text / file-alt
  file: 'fa-solid fa-file',
  folder: 'fa-solid fa-folder',
  folderOpen: 'fa-solid fa-folder-open',
  attachment: 'fa-solid fa-paperclip',
  note: 'fa-solid fa-note-sticky', // was: sticky-note
  notesMedical: 'fa-solid fa-notes-medical',
  book: 'fa-solid fa-book',
  stamp: 'fa-solid fa-stamp',
  certificate: 'fa-solid fa-certificate',
  list: 'fa-solid fa-list',
  tableList: 'fa-solid fa-table-list',
  rectangleList: 'fa-solid fa-rectangle-list',
  // Cruscotto a colonne (una colonna per stage): non è una tabella, sono corsie.
  board: 'fa-solid fa-table-columns',
  // Pratica a stage (motore processi): manutenzione guidata, assunzione, …
  process: 'fa-solid fa-diagram-project',
  // Rifornimento: carburante o ricarica elettrica.
  gasPump: 'fa-solid fa-gas-pump',
  // Multe e sinistri: il verbale è un foglio con timbro, il ricorso una bilancia,
  // la rivalsa sull'autista un calendario di rate.
  violation: 'fa-solid fa-file-circle-exclamation',
  contested: 'fa-solid fa-scale-balanced',
  crash: 'fa-solid fa-car-burst',
  installments: 'fa-solid fa-calendar-days',
  // Autista non più operativo. Distinto da `fa-user-slash`, che marca gli
  // autisti "fake": un concetto, un glifo.
  driverInactive: 'fa-solid fa-user-xmark',
  // Contratto applicato all'autista (CCNL, P.IVA): il foglio firmato del
  // rapporto di lavoro, distinto da `signature` (documento firmato generico).
  contract: 'fa-solid fa-file-contract',
  // Limitazione operativa dell'autista: ciò che NON può fare. Non `ban`
  // (annullamento) né `shield` (autorizzazione): un lucchetto sulla persona.
  limitation: 'fa-solid fa-user-lock',

  // ===== Communication =====
  email: 'fa-solid fa-envelope', // was: envelope-o
  comments: 'fa-solid fa-comments',
  phone: 'fa-solid fa-phone',
  headset: 'fa-solid fa-headset',
  satellite: 'fa-solid fa-satellite-dish',

  // ===== Canali di provenienza B2C (sito / BOT / APP) =====
  // Un concetto = un glifo: sono i tre canali consumer da cui arrivano ordini
  // e preventivi, usati sia nelle celle "Canale" sia nei titoli dei pannelli.
  channelWebsite: 'fa-solid fa-globe',
  channelBot: 'fa-solid fa-robot',
  channelApp: 'fa-solid fa-mobile-screen-button',

  // ===== Money & billing =====
  money: 'fa-solid fa-euro-sign',
  moneyBill: 'fa-solid fa-money-bill-wave', // was: money-bill
  moneyBag: 'fa-solid fa-sack-dollar',
  moneyCheck: 'fa-solid fa-money-check-dollar',
  reconciliation: 'fa-solid fa-dollar-sign',
  creditCard: 'fa-solid fa-credit-card',
  calculator: 'fa-solid fa-calculator',

  // ===== Maintenance / tools =====
  tools: 'fa-solid fa-toolbox',
  wrench: 'fa-solid fa-wrench',
  toolsWrench: 'fa-solid fa-screwdriver-wrench',

  // ===== Charts / analytics =====
  chartLine: 'fa-solid fa-chart-line',
  chartColumn: 'fa-solid fa-chart-column', // was: chart-bar
  gauge: 'fa-solid fa-gauge-high',

  // ===== Navigation & arrows =====
  chevronLeft: 'fa-solid fa-chevron-left', // was: angle-left
  chevronRight: 'fa-solid fa-chevron-right', // was: angle-right
  chevronDown: 'fa-solid fa-chevron-down',
  chevronUp: 'fa-solid fa-chevron-up',
  chevronsLeft: 'fa-solid fa-angles-left', // was: angle-double-left
  chevronsRight: 'fa-solid fa-angles-right', // was: angle-double-right
  caretRight: 'fa-solid fa-caret-right',
  arrowLeft: 'fa-solid fa-arrow-left',
  arrowRight: 'fa-solid fa-arrow-right',
  arrowUp: 'fa-solid fa-arrow-up',
  arrowDown: 'fa-solid fa-arrow-down',
  arrowRightLong: 'fa-solid fa-arrow-right-long',
  turnUp: 'fa-solid fa-turn-up',
  reply: 'fa-solid fa-reply',
  share: 'fa-solid fa-share',
  menu: 'fa-solid fa-bars',
  move: 'fa-solid fa-arrows-up-down-left-right',

  // ===== Misc / objects =====
  tag: 'fa-solid fa-tag',
  tags: 'fa-solid fa-tags',
  hashtag: 'fa-solid fa-hashtag',
  circle: 'fa-solid fa-circle',
  circleOutline: 'fa-regular fa-circle', // was: circle-thin (FA4)
  circleDot: 'fa-solid fa-circle-dot',
  dragHandle: 'fa-solid fa-grip-vertical',
  grip: 'fa-solid fa-grip',
  objectGroup: 'fa-solid fa-object-group',
  layerGroup: 'fa-solid fa-layer-group',
  puzzle: 'fa-solid fa-puzzle-piece',
  magic: 'fa-solid fa-wand-magic-sparkles', // was: fa-magic
  key: 'fa-solid fa-key',
  shield: 'fa-solid fa-shield-halved',
  pin: 'fa-solid fa-thumbtack',
  camera: 'fa-solid fa-camera',
  plug: 'fa-solid fa-plug',
  box: 'fa-solid fa-box',
  cart: 'fa-solid fa-cart-shopping', // was: shopping-cart
  addToCart: 'fa-solid fa-cart-plus',
  timeline: 'fa-solid fa-timeline',
  weight: 'fa-solid fa-weight-hanging',
  ruler: 'fa-solid fa-ruler-combined',
  peopleCarry: 'fa-solid fa-people-carry-box', // was: people-carry
  handsHelping: 'fa-solid fa-hands-holding-child', // was: hands-helping
  sun: 'fa-solid fa-sun',
  moon: 'fa-solid fa-moon',
  beach: 'fa-solid fa-umbrella-beach',
  coffee: 'fa-solid fa-mug-hot',
  mask: 'fa-solid fa-head-side-mask',
  battery: 'fa-solid fa-battery-full',
  shoePrints: 'fa-solid fa-shoe-prints',
};

// Icon names available in the registry (for validation / autocomplete tooling).
export const ICON_NAMES = Object.keys(ICONS);

// Build a full FA class string from an icon token that may be either a bare glyph
// (`fa-user`) or an already-styled class (`fa-solid fa-user`, e.g. an ICONS value).
// Adds the default `fa-solid` style prefix only when none is present, so callers can
// pass registry values or legacy bare glyphs interchangeably without double-prefixing.
export const faClass = (icon = '', extra = '') => {
  const hasStyle = /\bfa-(solid|regular|brands)\b/.test(icon);
  return `${hasStyle ? '' : 'fa-solid '}${icon}${extra ? ` ${extra}` : ''}`.trim();
};
